/** Demo Mode only - single reconciled finance dataset backing the consolidated Finance
 * area (Overview / Forecast / Profit & Loss / Contracts / EBIT Analysis - see
 * src/pages/Finance). No backend financial-forecast/P&L/EBIT feed exists yet.
 *
 * IMPLEMENTATION PLAN (kept here, not as a stray planning markdown file):
 * - Retained: src/components/shared/finance/useLiveOperationsPnL.ts (still powers
 *   OperationalDays > LiveOperationsPnLCard, a live-clock single-store card unrelated
 *   to this contract/company financial-year dataset - left untouched).
 * - Consolidated/redirected: the old src/pages/FinanceDashboard, src/pages/ProfitAndLoss
 *   and src/pages/CommercialPerformance standalone pages are removed. Their old routes
 *   (/finance-dashboard, /profit-and-loss, /commercial-performance) now redirect into
 *   the new /finance-performance area (see src/App.tsx). FinanceDashboard's "site finance
 *   operations" invoice/credit/delivery-reconciliation exception tiles are preserved as a
 *   section on the new Overview tab (still reading src/mocks/data/deliveryReconciliationData.ts).
 *   useProfitAndLossForecast.ts (only ever used by the removed ProfitAndLoss page) is removed.
 * - New reusable pieces: this dataset module; FinanceScopeContext (Company/Client/Contract/
 *   Site scope, URL-search-param backed); FinanceFilterBar; MetricTile; TrendChart;
 *   ProfitAndLossTable (expandable, scope-aware); ContractPerformanceTable; EbitBridgeChart;
 *   FinanceExceptionCards - all under src/pages/Finance/components.
 * - Assumptions/limitations: figures are deterministic, generated from a small set of
 *   per-contract ratio assumptions (not hand-typed line items) specifically so contract-level
 *   totals always reconcile to company-level totals by construction (see `reconcileCheck`
 *   below and financeData.test.ts). Monthly phasing uses a flat margin-per-month
 *   approximation on top of a seasonal revenue curve (lazy: real monthly cost variation
 *   isn't modelled - upgrade path is per-month ratio overrides once real data lands).
 *   Financial year is fixed to Apr-Mar with the "current" period fixed at P8 (November) so
 *   the demo always shows a stable YTD-actual / forecast-remainder split.
 */

export type ContractSector = 'Education' | 'Workplace catering' | 'Sports & leisure' | 'Care' | 'Hospitality & events';
export type CommercialModel = 'Cost plus' | 'Fixed price' | 'Profit & loss share' | 'Management fee';
export type FinancialStatus = 'Ahead of plan' | 'On plan' | 'At risk' | 'Materially behind plan';
export type FinanceScenario = 'actual' | 'budget' | 'previousForecast' | 'currentForecast' | 'priorYear';

export const FINANCIAL_YEAR_LABEL = 'FY 2025/26';
export const PERIOD_LABELS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
/** 0-based index of the last completed period - P8 (Nov). Months after this are forecast. */
export const CURRENT_PERIOD_INDEX = 7;

export interface PnLLineItems {
  foodSales: number;
  hospitalitySales: number;
  managementFees: number;
  clientSubsidies: number;
  otherIncome: number;
  foodAndBeverage: number;
  consumables: number;
  waste: number;
  supplierRebates: number;
  salariesAndWages: number;
  agencyLabour: number;
  overtime: number;
  employerCosts: number;
  equipment: number;
  cleaning: number;
  utilities: number;
  travel: number;
  training: number;
  centralCharges: number;
  otherOpex: number;
  allocatedOverhead: number;
}

export interface PnLTotals extends PnLLineItems {
  totalRevenue: number;
  totalCostOfSales: number;
  grossProfit: number;
  grossProfitPercentage: number;
  totalLabour: number;
  labourPercentage: number;
  totalOpex: number;
  contribution: number;
  ebit: number;
  ebitMarginPercentage: number;
}

const LINE_ITEM_KEYS: (keyof PnLLineItems)[] = [
  'foodSales', 'hospitalitySales', 'managementFees', 'clientSubsidies', 'otherIncome',
  'foodAndBeverage', 'consumables', 'waste', 'supplierRebates',
  'salariesAndWages', 'agencyLabour', 'overtime', 'employerCosts',
  'equipment', 'cleaning', 'utilities', 'travel', 'training', 'centralCharges', 'otherOpex',
  'allocatedOverhead',
];

const emptyLineItems = (): PnLLineItems => LINE_ITEM_KEYS.reduce((acc, key) => {
  acc[key] = 0;
  return acc;
}, {} as PnLLineItems);

/** Adds two line-item sets - used to build company/sector/client rollups so totals always
 * equal the sum of their constituent contracts (rather than being independently computed). */
export const sumLineItems = (items: PnLLineItems[]): PnLLineItems => items.reduce((acc, item) => {
  LINE_ITEM_KEYS.forEach((key) => {
    acc[key] += item[key];
  });
  return acc;
}, emptyLineItems());

export const computeTotals = (items: PnLLineItems): PnLTotals => {
  const totalRevenue = items.foodSales + items.hospitalitySales + items.managementFees + items.clientSubsidies + items.otherIncome;
  const totalCostOfSales = items.foodAndBeverage + items.consumables + items.waste + items.supplierRebates;
  const grossProfit = totalRevenue - totalCostOfSales;
  const totalLabour = items.salariesAndWages + items.agencyLabour + items.overtime + items.employerCosts;
  const totalOpex = items.equipment + items.cleaning + items.utilities + items.travel + items.training + items.centralCharges + items.otherOpex;
  const contribution = grossProfit - totalLabour - totalOpex;
  const ebit = contribution - items.allocatedOverhead;
  return {
    ...items,
    totalRevenue,
    totalCostOfSales,
    grossProfit,
    grossProfitPercentage: totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0,
    totalLabour,
    labourPercentage: totalRevenue > 0 ? (totalLabour / totalRevenue) * 100 : 0,
    totalOpex,
    contribution,
    ebit,
    ebitMarginPercentage: totalRevenue > 0 ? (ebit / totalRevenue) * 100 : 0,
  };
};

interface Mix {
  [key: string]: number;
}

interface ScenarioDelta {
  revenuePct: number;
  foodCostPtsDelta: number;
  labourPtsDelta: number;
  opexPtsDelta: number;
}

interface ContractAssumptions {
  id: string;
  client: string;
  contractName: string;
  site: string;
  sector: ContractSector;
  region: string;
  commercialModel: CommercialModel;
  mobilisedDate: string;
  situation: string;
  annualBudgetRevenue: number;
  incomeMix: { foodSales: number; hospitalitySales: number; managementFees: number; clientSubsidies: number; otherIncome: number };
  budgetFoodCostPct: number;
  budgetLabourPct: number;
  budgetOpexPct: number;
  overheadRatePct: number;
  costMix: { foodAndBeverage: number; consumables: number; waste: number; supplierRebates: number };
  labourMix: { salariesAndWages: number; agencyLabour: number; overtime: number; employerCosts: number };
  opexMix: { equipment: number; cleaning: number; utilities: number; travel: number; training: number; centralCharges: number; otherOpex: number };
  seasonalCurve: number[];
  scenarioDeltas: Record<Exclude<FinanceScenario, 'budget'>, ScenarioDelta>;
}

const DEFAULT_COST_MIX = { foodAndBeverage: 0.78, consumables: 0.16, waste: 0.09, supplierRebates: -0.03 };
const DEFAULT_LABOUR_MIX = { salariesAndWages: 0.74, agencyLabour: 0.08, overtime: 0.06, employerCosts: 0.12 };
const DEFAULT_OPEX_MIX = { equipment: 0.16, cleaning: 0.14, utilities: 0.18, travel: 0.06, training: 0.08, centralCharges: 0.28, otherOpex: 0.1 };
const FLAT_CURVE = new Array(12).fill(1);
/** Term-time weighting (low in summer holidays Jul/Aug, dips at Christmas). */
const EDUCATION_CURVE = [1.1, 1.15, 1.1, 0.35, 0.2, 1.05, 1.1, 1.15, 0.55, 1.1, 1.15, 0.95];
/** Event/hospitality peaks around summer and Christmas party season. */
const HOSPITALITY_CURVE = [0.75, 0.8, 0.85, 1.3, 1.35, 0.9, 0.85, 0.8, 1.9, 1.1, 0.85, 0.75];

const applyMix = (total: number, mix: Mix): Record<string, number> => Object.keys(mix).reduce((acc, key) => {
  acc[key] = total * mix[key];
  return acc;
}, {} as Record<string, number>);

const buildScenarioLineItems = (assumptions: ContractAssumptions, delta: ScenarioDelta): PnLLineItems => {
  const totalRevenue = assumptions.annualBudgetRevenue * (1 + delta.revenuePct / 100);
  const foodCostPct = Math.max(0, assumptions.budgetFoodCostPct + delta.foodCostPtsDelta);
  const labourPct = Math.max(0, assumptions.budgetLabourPct + delta.labourPtsDelta);
  const opexPct = Math.max(0, assumptions.budgetOpexPct + delta.opexPtsDelta);

  const totalCostOfSales = totalRevenue * (foodCostPct / 100);
  const totalLabour = totalRevenue * (labourPct / 100);
  const totalOpex = totalRevenue * (opexPct / 100);
  const allocatedOverhead = totalRevenue * (assumptions.overheadRatePct / 100);

  const income = applyMix(totalRevenue, assumptions.incomeMix);
  const cost = applyMix(totalCostOfSales, assumptions.costMix);
  const labour = applyMix(totalLabour, assumptions.labourMix);
  const opex = applyMix(totalOpex, assumptions.opexMix);

  return {
    foodSales: income.foodSales,
    hospitalitySales: income.hospitalitySales,
    managementFees: income.managementFees,
    clientSubsidies: income.clientSubsidies,
    otherIncome: income.otherIncome,
    foodAndBeverage: cost.foodAndBeverage,
    consumables: cost.consumables,
    waste: cost.waste,
    supplierRebates: cost.supplierRebates,
    salariesAndWages: labour.salariesAndWages,
    agencyLabour: labour.agencyLabour,
    overtime: labour.overtime,
    employerCosts: labour.employerCosts,
    equipment: opex.equipment,
    cleaning: opex.cleaning,
    utilities: opex.utilities,
    travel: opex.travel,
    training: opex.training,
    centralCharges: opex.centralCharges,
    otherOpex: opex.otherOpex,
    allocatedOverhead,
  };
};

export interface MonthlyTrend {
  revenue: number[];
  grossProfit: number[];
  ebit: number[];
}

const buildMonthlyTrend = (assumptions: ContractAssumptions, delta: ScenarioDelta): MonthlyTrend => {
  const totals = computeTotals(buildScenarioLineItems(assumptions, delta));
  const revenue = assumptions.seasonalCurve.map((weight) => (totals.totalRevenue * weight) / 12);
  const grossProfit = revenue.map((r) => r * (totals.grossProfitPercentage / 100));
  const ebit = revenue.map((r) => r * (totals.ebitMarginPercentage / 100));
  return { revenue, grossProfit, ebit };
};

export interface FinanceContract {
  id: string;
  client: string;
  contractName: string;
  site: string;
  sector: ContractSector;
  region: string;
  commercialModel: CommercialModel;
  mobilisedDate: string;
  situation: string;
  scenarios: Record<FinanceScenario, PnLLineItems>;
  monthly: Record<'budget' | 'currentForecast' | 'priorYear', MonthlyTrend>;
  /** Actual-to-date months blended with forecast-remainder months, for "now transitioning
   * to forecast" trend charts. Length 12; index <= CURRENT_PERIOD_INDEX is actual. */
  blendedTrend: MonthlyTrend;
}

const buildContract = (assumptions: ContractAssumptions): FinanceContract => {
  const scenarios: Record<FinanceScenario, PnLLineItems> = {
    budget: buildScenarioLineItems(assumptions, { revenuePct: 0, foodCostPtsDelta: 0, labourPtsDelta: 0, opexPtsDelta: 0 }),
    actual: buildScenarioLineItems(assumptions, assumptions.scenarioDeltas.actual),
    previousForecast: buildScenarioLineItems(assumptions, assumptions.scenarioDeltas.previousForecast),
    currentForecast: buildScenarioLineItems(assumptions, assumptions.scenarioDeltas.currentForecast),
    priorYear: buildScenarioLineItems(assumptions, assumptions.scenarioDeltas.priorYear),
  };

  const monthly = {
    budget: buildMonthlyTrend(assumptions, { revenuePct: 0, foodCostPtsDelta: 0, labourPtsDelta: 0, opexPtsDelta: 0 }),
    currentForecast: buildMonthlyTrend(assumptions, assumptions.scenarioDeltas.currentForecast),
    priorYear: buildMonthlyTrend(assumptions, assumptions.scenarioDeltas.priorYear),
  };
  const actualMonthly = buildMonthlyTrend(assumptions, assumptions.scenarioDeltas.actual);

  const blendedTrend: MonthlyTrend = {
    revenue: monthly.currentForecast.revenue.map((v, i) => (i <= CURRENT_PERIOD_INDEX ? actualMonthly.revenue[i] : v)),
    grossProfit: monthly.currentForecast.grossProfit.map((v, i) => (i <= CURRENT_PERIOD_INDEX ? actualMonthly.grossProfit[i] : v)),
    ebit: monthly.currentForecast.ebit.map((v, i) => (i <= CURRENT_PERIOD_INDEX ? actualMonthly.ebit[i] : v)),
  };

  return {
    id: assumptions.id,
    client: assumptions.client,
    contractName: assumptions.contractName,
    site: assumptions.site,
    sector: assumptions.sector,
    region: assumptions.region,
    commercialModel: assumptions.commercialModel,
    mobilisedDate: assumptions.mobilisedDate,
    situation: assumptions.situation,
    scenarios,
    monthly,
    blendedTrend,
  };
};

const CONTRACT_ASSUMPTIONS: ContractAssumptions[] = [
  {
    id: 'stmarys-education', client: 'St Mary\u2019s Multi-Academy Trust', contractName: 'Education Catering', site: 'Downtowner Academy', sector: 'Education', region: 'North West', commercialModel: 'Cost plus', mobilisedDate: '2021-09-01', situation: 'Strong performer',
    annualBudgetRevenue: 1650000,
    incomeMix: { foodSales: 0.82, hospitalitySales: 0.04, managementFees: 0.1, clientSubsidies: 0.03, otherIncome: 0.01 },
    budgetFoodCostPct: 30, budgetLabourPct: 24, budgetOpexPct: 9, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: EDUCATION_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: 4.2, foodCostPtsDelta: -1, labourPtsDelta: -0.5, opexPtsDelta: 0 },
      previousForecast: { revenuePct: 2.5, foodCostPtsDelta: -0.5, labourPtsDelta: -0.2, opexPtsDelta: 0 },
      currentForecast: { revenuePct: 4, foodCostPtsDelta: -1, labourPtsDelta: -0.5, opexPtsDelta: 0 },
      priorYear: { revenuePct: -6, foodCostPtsDelta: 0.5, labourPtsDelta: 0.3, opexPtsDelta: 0 },
    },
  },
  {
    id: 'northgate-care', client: 'Northgate NHS Trust', contractName: 'Patient & Staff Catering', site: 'Riverside Site', sector: 'Care', region: 'Yorkshire & Humber', commercialModel: 'Management fee', mobilisedDate: '2019-04-01', situation: 'Food cost pressure',
    annualBudgetRevenue: 1250000,
    incomeMix: { foodSales: 0.62, hospitalitySales: 0.02, managementFees: 0.3, clientSubsidies: 0.05, otherIncome: 0.01 },
    budgetFoodCostPct: 32, budgetLabourPct: 27, budgetOpexPct: 9, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: -1.2, foodCostPtsDelta: 2.5, labourPtsDelta: 0.4, opexPtsDelta: 0 },
      previousForecast: { revenuePct: -0.5, foodCostPtsDelta: 1.5, labourPtsDelta: 0.2, opexPtsDelta: 0 },
      currentForecast: { revenuePct: -1, foodCostPtsDelta: 2.5, labourPtsDelta: 0.4, opexPtsDelta: 0 },
      priorYear: { revenuePct: -4, foodCostPtsDelta: 0.5, labourPtsDelta: 0, opexPtsDelta: 0 },
    },
  },
  {
    id: 'ridgeway-workplace', client: 'Ridgeway Council', contractName: 'Corporate Dining Services', site: 'Uptown Civic Centre', sector: 'Workplace catering', region: 'South East', commercialModel: 'Profit & loss share', mobilisedDate: '2018-01-01', situation: 'Loss-making',
    annualBudgetRevenue: 950000,
    incomeMix: { foodSales: 0.86, hospitalitySales: 0.08, managementFees: 0.04, clientSubsidies: 0.01, otherIncome: 0.01 },
    budgetFoodCostPct: 33, budgetLabourPct: 50, budgetOpexPct: 10, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: -9, foodCostPtsDelta: 1.5, labourPtsDelta: 3, opexPtsDelta: 1 },
      previousForecast: { revenuePct: -5, foodCostPtsDelta: 1, labourPtsDelta: 2, opexPtsDelta: 0.5 },
      currentForecast: { revenuePct: -9, foodCostPtsDelta: 1.5, labourPtsDelta: 3, opexPtsDelta: 1 },
      priorYear: { revenuePct: -2, foodCostPtsDelta: 0.5, labourPtsDelta: 1, opexPtsDelta: 0 },
    },
  },
  {
    id: 'fairview-workplace', client: 'Fairview Holdings', contractName: 'Retail & Concessions', site: 'Southgate Campus', sector: 'Workplace catering', region: 'South East', commercialModel: 'Fixed price', mobilisedDate: '2020-06-01', situation: 'On plan',
    annualBudgetRevenue: 780000,
    incomeMix: { foodSales: 0.9, hospitalitySales: 0.05, managementFees: 0.03, clientSubsidies: 0.01, otherIncome: 0.01 },
    budgetFoodCostPct: 29, budgetLabourPct: 25, budgetOpexPct: 8, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: 1.2, foodCostPtsDelta: 0.1, labourPtsDelta: -0.1, opexPtsDelta: 0 },
      previousForecast: { revenuePct: 0.8, foodCostPtsDelta: 0.2, labourPtsDelta: 0, opexPtsDelta: 0 },
      currentForecast: { revenuePct: 1, foodCostPtsDelta: 0.1, labourPtsDelta: -0.1, opexPtsDelta: 0 },
      priorYear: { revenuePct: -3, foodCostPtsDelta: 0.3, labourPtsDelta: 0.2, opexPtsDelta: 0 },
    },
  },
  {
    id: 'oakleigh-education', client: 'Oakleigh Academy Trust', contractName: 'Meadowbrook Catering Mobilisation', site: 'Meadowbrook Academy', sector: 'Education', region: 'East Midlands', commercialModel: 'Cost plus', mobilisedDate: '2025-07-01', situation: 'Newly-mobilised, behind ramp-up',
    annualBudgetRevenue: 520000,
    incomeMix: { foodSales: 0.84, hospitalitySales: 0.03, managementFees: 0.1, clientSubsidies: 0.02, otherIncome: 0.01 },
    budgetFoodCostPct: 31, budgetLabourPct: 26, budgetOpexPct: 11, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: EDUCATION_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: -12, foodCostPtsDelta: 1.8, labourPtsDelta: 4.5, opexPtsDelta: 2 },
      previousForecast: { revenuePct: -18, foodCostPtsDelta: 2.5, labourPtsDelta: 6, opexPtsDelta: 3 },
      currentForecast: { revenuePct: -10, foodCostPtsDelta: 1.5, labourPtsDelta: 3.5, opexPtsDelta: 1.5 },
      priorYear: { revenuePct: -100, foodCostPtsDelta: 0, labourPtsDelta: 0, opexPtsDelta: 0 },
    },
  },
  {
    id: 'precision-workplace', client: 'Precision Dynamics Ltd', contractName: 'HQ Campus Dining', site: 'HQ Campus', sector: 'Workplace catering', region: 'South West', commercialModel: 'Management fee', mobilisedDate: '2017-03-01', situation: 'Labour cost pressure',
    annualBudgetRevenue: 1450000,
    incomeMix: { foodSales: 0.7, hospitalitySales: 0.1, managementFees: 0.17, clientSubsidies: 0.02, otherIncome: 0.01 },
    budgetFoodCostPct: 30, budgetLabourPct: 26, budgetOpexPct: 9, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: { salariesAndWages: 0.68, agencyLabour: 0.14, overtime: 0.07, employerCosts: 0.11 }, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: -0.5, foodCostPtsDelta: 0.3, labourPtsDelta: 3.5, opexPtsDelta: 0 },
      previousForecast: { revenuePct: 0, foodCostPtsDelta: 0.2, labourPtsDelta: 2, opexPtsDelta: 0 },
      currentForecast: { revenuePct: -0.5, foodCostPtsDelta: 0.3, labourPtsDelta: 3.5, opexPtsDelta: 0 },
      priorYear: { revenuePct: -3, foodCostPtsDelta: 0, labourPtsDelta: 1, opexPtsDelta: 0 },
    },
  },
  {
    id: 'crestview-sports', client: 'Crestview Leisure Group', contractName: 'Aquatic & Fitness Catering', site: 'Aquatic Centre', sector: 'Sports & leisure', region: 'North East', commercialModel: 'Profit & loss share', mobilisedDate: '2022-02-01', situation: 'Improving contract',
    annualBudgetRevenue: 680000,
    incomeMix: { foodSales: 0.75, hospitalitySales: 0.18, managementFees: 0.04, clientSubsidies: 0.02, otherIncome: 0.01 },
    budgetFoodCostPct: 31, budgetLabourPct: 27, budgetOpexPct: 10, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: 2, foodCostPtsDelta: -0.3, labourPtsDelta: -0.5, opexPtsDelta: 0 },
      previousForecast: { revenuePct: -5, foodCostPtsDelta: 1.5, labourPtsDelta: 1.5, opexPtsDelta: 0.5 },
      currentForecast: { revenuePct: 2.5, foodCostPtsDelta: -0.3, labourPtsDelta: -0.5, opexPtsDelta: 0 },
      priorYear: { revenuePct: -8, foodCostPtsDelta: 1, labourPtsDelta: 1, opexPtsDelta: 0.5 },
    },
  },
  {
    id: 'silverwood-care', client: 'Silverwood Care Homes', contractName: 'Residential Care Catering', site: 'Willow Court', sector: 'Care', region: 'Wales', commercialModel: 'Cost plus', mobilisedDate: '2016-08-01', situation: 'Deteriorating forecast',
    annualBudgetRevenue: 890000,
    incomeMix: { foodSales: 0.88, hospitalitySales: 0.02, managementFees: 0.07, clientSubsidies: 0.02, otherIncome: 0.01 },
    budgetFoodCostPct: 32, budgetLabourPct: 27, budgetOpexPct: 9, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: -3, foodCostPtsDelta: 2, labourPtsDelta: 0.5, opexPtsDelta: 0 },
      previousForecast: { revenuePct: -1, foodCostPtsDelta: 0.8, labourPtsDelta: 0.3, opexPtsDelta: 0 },
      currentForecast: { revenuePct: -3.5, foodCostPtsDelta: 2.6, labourPtsDelta: 0.6, opexPtsDelta: 0 },
      priorYear: { revenuePct: -5, foodCostPtsDelta: 0.5, labourPtsDelta: 0, opexPtsDelta: 0 },
    },
  },
  {
    id: 'granby-hospitality', client: 'Granby Events & Hospitality', contractName: 'City Pavilion Events Catering', site: 'City Pavilion', sector: 'Hospitality & events', region: 'Greater London', commercialModel: 'Profit & loss share', mobilisedDate: '2015-05-01', situation: 'Seasonal event revenue spikes',
    annualBudgetRevenue: 2100000,
    incomeMix: { foodSales: 0.35, hospitalitySales: 0.55, managementFees: 0.06, clientSubsidies: 0.02, otherIncome: 0.02 },
    budgetFoodCostPct: 28, budgetLabourPct: 23, budgetOpexPct: 11, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: HOSPITALITY_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: 15, foodCostPtsDelta: 0.5, labourPtsDelta: 0.8, opexPtsDelta: 0.5 },
      previousForecast: { revenuePct: 8, foodCostPtsDelta: 0.5, labourPtsDelta: 0.5, opexPtsDelta: 0.3 },
      currentForecast: { revenuePct: 15, foodCostPtsDelta: 0.5, labourPtsDelta: 0.8, opexPtsDelta: 0.5 },
      priorYear: { revenuePct: 3, foodCostPtsDelta: 0, labourPtsDelta: 0, opexPtsDelta: 0 },
    },
  },
  {
    id: 'thornfield-sports', client: 'Thornfield Sports Trust', contractName: 'Thornfield Stadium Catering', site: 'Thornfield Stadium', sector: 'Sports & leisure', region: 'North West', commercialModel: 'Fixed price', mobilisedDate: '2019-11-01', situation: 'On plan',
    annualBudgetRevenue: 1050000,
    incomeMix: { foodSales: 0.68, hospitalitySales: 0.24, managementFees: 0.05, clientSubsidies: 0.02, otherIncome: 0.01 },
    budgetFoodCostPct: 29, budgetLabourPct: 25, budgetOpexPct: 9, overheadRatePct: 4,
    costMix: DEFAULT_COST_MIX, labourMix: DEFAULT_LABOUR_MIX, opexMix: DEFAULT_OPEX_MIX, seasonalCurve: FLAT_CURVE,
    scenarioDeltas: {
      actual: { revenuePct: 0.5, foodCostPtsDelta: 0, labourPtsDelta: 0.2, opexPtsDelta: 0 },
      previousForecast: { revenuePct: 0.3, foodCostPtsDelta: 0.1, labourPtsDelta: 0.1, opexPtsDelta: 0 },
      currentForecast: { revenuePct: 0.5, foodCostPtsDelta: 0, labourPtsDelta: 0.2, opexPtsDelta: 0 },
      priorYear: { revenuePct: -4, foodCostPtsDelta: 0.3, labourPtsDelta: 0, opexPtsDelta: 0 },
    },
  },
];

export const FINANCE_CONTRACTS: FinanceContract[] = CONTRACT_ASSUMPTIONS.map(buildContract);

/** Company-level rollup for a scenario - always the sum of every contract's line items for
 * that scenario, so company totals reconcile with contract-level data by construction. */
export const companyScenarioTotals = (scenario: FinanceScenario): PnLTotals => computeTotals(
  sumLineItems(FINANCE_CONTRACTS.map((contract) => contract.scenarios[scenario])),
);

export const companyMonthlyTrend = (series: 'budget' | 'currentForecast' | 'priorYear'): MonthlyTrend => {
  const revenue = new Array(12).fill(0);
  const grossProfit = new Array(12).fill(0);
  const ebit = new Array(12).fill(0);
  FINANCE_CONTRACTS.forEach((contract) => {
    contract.monthly[series].revenue.forEach((v, i) => { revenue[i] += v; });
    contract.monthly[series].grossProfit.forEach((v, i) => { grossProfit[i] += v; });
    contract.monthly[series].ebit.forEach((v, i) => { ebit[i] += v; });
  });
  return { revenue, grossProfit, ebit };
};

export const companyBlendedTrend = (): MonthlyTrend => {
  const revenue = new Array(12).fill(0);
  const grossProfit = new Array(12).fill(0);
  const ebit = new Array(12).fill(0);
  FINANCE_CONTRACTS.forEach((contract) => {
    contract.blendedTrend.revenue.forEach((v, i) => { revenue[i] += v; });
    contract.blendedTrend.grossProfit.forEach((v, i) => { grossProfit[i] += v; });
    contract.blendedTrend.ebit.forEach((v, i) => { ebit[i] += v; });
  });
  return { revenue, grossProfit, ebit };
};

/** Financial status classification used by the Contracts tab, driven off the same
 * scenario totals everything else uses (no separate/duplicated risk numbers). */
export const classifyFinancialStatus = (contract: FinanceContract): FinancialStatus => {
  const budget = computeTotals(contract.scenarios.budget);
  const forecast = computeTotals(contract.scenarios.currentForecast);
  const marginDeltaPts = forecast.ebitMarginPercentage - budget.ebitMarginPercentage;
  if (forecast.ebit < 0 || marginDeltaPts < -3) return 'Materially behind plan';
  if (marginDeltaPts < -1) return 'At risk';
  if (marginDeltaPts >= 1) return 'Ahead of plan';
  return 'On plan';
};

/** Self-check (lazy: runtime assert, not a full reconciliation engine) - guards the core
 * acceptance criteria that contract-level figures always sum to company-level totals.
 * Runs at import time outside production so a future edit that breaks the sum-based
 * construction fails fast in dev/test rather than silently drifting. See financeData.test.ts
 * for the same check as a proper Jest assertion. */
const reconcileCheck = () => {
  (['actual', 'budget', 'previousForecast', 'currentForecast', 'priorYear'] as FinanceScenario[]).forEach((scenario) => {
    const company = companyScenarioTotals(scenario);
    const summedRevenue = FINANCE_CONTRACTS.reduce((sum, c) => sum + computeTotals(c.scenarios[scenario]).totalRevenue, 0);
    const summedEbit = FINANCE_CONTRACTS.reduce((sum, c) => sum + computeTotals(c.scenarios[scenario]).ebit, 0);
    if (Math.abs(company.totalRevenue - summedRevenue) > 0.01 || Math.abs(company.ebit - summedEbit) > 0.01) {
      throw new Error(`Finance dataset reconciliation failed for scenario "${scenario}" - contract totals do not sum to company totals.`);
    }
  });
};

if (process.env.NODE_ENV !== 'production') {
  reconcileCheck();
}
